import React, { useState } from 'react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input, { Select } from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Table from '../../components/ui/Table';
import { useToast } from '../../components/ui/Toast';
import { auditLog } from '../../data/mockData';
import { Save, Shield, Globe, Bell } from 'lucide-react';

const roles = [
  { id: 'r1', name: 'Суперадмин', code: 'superadmin', users: 1 },
  { id: 'r2', name: 'Администратор магазина', code: 'store_admin', users: 5 },
  { id: 'r3', name: 'Менеджер', code: 'manager', users: 8 },
  { id: 'r4', name: 'Наблюдатель', code: 'viewer', users: 3 },
];

const permissions = [
  { module: 'Дашборд', superadmin: true, store_admin: true, manager: true, viewer: true },
  { module: 'Сотрудники', superadmin: true, store_admin: true, manager: false, viewer: false },
  { module: 'Магазины', superadmin: true, store_admin: true, manager: false, viewer: false },
  { module: 'Товары', superadmin: true, store_admin: true, manager: true, viewer: true },
  { module: 'Задачи', superadmin: true, store_admin: true, manager: true, viewer: false },
  { module: 'ОСГ Проверки', superadmin: true, store_admin: true, manager: true, viewer: true },
  { module: 'Выкладка', superadmin: true, store_admin: true, manager: true, viewer: true },
  { module: 'Инвентаризация', superadmin: true, store_admin: true, manager: true, viewer: false },
  { module: 'Аналитика', superadmin: true, store_admin: true, manager: true, viewer: true },
  { module: 'Уведомления', superadmin: true, store_admin: true, manager: false, viewer: false },
  { module: 'Настройки', superadmin: true, store_admin: false, manager: false, viewer: false },
];

export default function Settings() {
  const toast = useToast();
  const [view, setView] = useState('general');
  const [settings, setSettings] = useState({
    companyName: 'OSG Retail Network',
    currency: 'сум',
    language: 'ru',
    expiryWarning: 7,
    expiryCritical: 3,
    maxDiscount: 30,
  });

  const auditColumns = [
    { key: 'timestamp', label: 'Время', render: v => <span style={{ fontSize: 12, color: '#6B7280' }}>{new Date(v).toLocaleString('ru-RU')}</span> },
    { key: 'user', label: 'Пользователь', render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'action', label: 'Действие', render: v => <Badge variant={v === 'CREATE' ? 'success' : v === 'DELETE' ? 'error' : 'primary'}>{v}</Badge> },
    { key: 'entity', label: 'Сущность', render: v => <Badge variant="gray">{v}</Badge> },
    { key: 'detail', label: 'Детали', render: v => <span style={{ fontSize: 12, color: '#374151' }}>{v}</span> },
  ];

  const tabs = [
    { key: 'general', label: '⚙️ Общие настройки' },
    { key: 'roles', label: '🔐 Роли и права' },
    { key: 'audit', label: '📋 Журнал событий' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1A1A2E' }}>Настройки</h1>
        <p style={{ color: '#8E8E93', fontSize: 13, marginTop: 2 }}>Управление системой и правами доступа</p>
      </div>

      <div style={{ display: 'flex', gap: 4, background: '#fff', padding: '6px', borderRadius: 12, width: 'fit-content', border: '1px solid #F0F0F0' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setView(t.key)} style={{
            padding: '7px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: view === t.key ? 700 : 500,
            background: view === t.key ? '#4A6CF7' : 'transparent',
            color: view === t.key ? '#fff' : '#6B7280', fontFamily: 'inherit',
          }}>{t.label}</button>
        ))}
      </div>

      {view === 'general' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 600 }}>
          <Card title="🏢 Компания">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Input label="Название компании" value={settings.companyName} onChange={e => setSettings(s => ({ ...s, companyName: e.target.value }))} />
              <Select label="Язык интерфейса" value={settings.language} onChange={e => setSettings(s => ({ ...s, language: e.target.value }))} options={[
                { value: 'ru', label: '🇷🇺 Русский' },
                { value: 'uz', label: '🇺🇿 O\'zbek' },
                { value: 'en', label: '🇬🇧 English' },
              ]} />
              <Select label="Валюта" value={settings.currency} onChange={e => setSettings(s => ({ ...s, currency: e.target.value }))} options={[
                { value: 'сум', label: "сум (so'm)" },
                { value: 'UZS', label: 'UZS' },
              ]} />
              <Button icon={<Save size={14} />} onClick={() => toast('Настройки сохранены', 'success')}>Сохранить</Button>
            </div>
          </Card>

          <Card title="⏰ Пороги ОСГ">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Input label="Предупреждение (дней до истечения)" type="number" value={settings.expiryWarning} onChange={e => setSettings(s => ({ ...s, expiryWarning: e.target.value }))} />
              <Input label="Критический порог (дней до истечения)" type="number" value={settings.expiryCritical} onChange={e => setSettings(s => ({ ...s, expiryCritical: e.target.value }))} />
              <Input label="Максимальный % скидки по умолчанию" type="number" value={settings.maxDiscount} onChange={e => setSettings(s => ({ ...s, maxDiscount: e.target.value }))} />
              <Button icon={<Save size={14} />} onClick={() => toast('Настройки ОСГ сохранены', 'success')}>Сохранить</Button>
            </div>
          </Card>
        </div>
      )}

      {view === 'roles' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
            {roles.map(r => (
              <div key={r.id} style={{ background: '#fff', borderRadius: 12, padding: '16px 18px', border: '1px solid #F0F0F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <Shield size={18} color="#4A6CF7" />
                  <span style={{ fontWeight: 700, fontSize: 14 }}>{r.name}</span>
                </div>
                <code style={{ fontSize: 11, color: '#4A6CF7', background: '#EEF2FF', padding: '2px 8px', borderRadius: 4 }}>{r.code}</code>
                <div style={{ marginTop: 8, fontSize: 12, color: '#6B7280' }}>{r.users} пользователей</div>
              </div>
            ))}
          </div>

          <Card title="Матрица прав доступа" padding="0">
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#F8F9FB' }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#6B7280', borderBottom: '1px solid #F0F0F0' }}>Модуль</th>
                    {roles.map(r => (
                      <th key={r.id} style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, color: '#6B7280', borderBottom: '1px solid #F0F0F0' }}>{r.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {permissions.map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #F3F4F6' }}>
                      <td style={{ padding: '10px 16px', fontWeight: 500 }}>{p.module}</td>
                      {roles.map(r => (
                        <td key={r.id} style={{ padding: '10px 16px', textAlign: 'center' }}>
                          <span style={{ fontSize: 16 }}>{p[r.code] ? '✅' : '❌'}</span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {view === 'audit' && (
        <Card title="Журнал системных событий" padding="0">
          <Table columns={auditColumns} data={auditLog} emptyText="Нет событий" />
        </Card>
      )}
    </div>
  );
}
